import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { TEL_HREF, PHONE_DISPLAY, WA_HREF_TEXT, WA_HREF_TEXT_EN, LOGO_URL } from "@/constants/site";
import { EASE } from "./Reveal";

const useLinks = (t) => [
  { href: "#chi-siamo", label: t.nav.about, testid: "nav-about-link" },
  { href: "#menu", label: t.nav.menu, testid: "nav-menu-link" },
  { href: "#perche-noi", label: t.nav.highlights, testid: "nav-highlights-link" },
  { href: "#contatti", label: t.nav.contacts, testid: "nav-contacts-link" },
];

export const Nav = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const links = useLinks(t);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || open;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-cream/90 backdrop-blur-md border-b border-stoneMuted/15" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-6 h-16 sm:h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5 font-serif text-lg sm:text-xl tracking-wide leading-none" data-testid="nav-brand-link">
            <img src={LOGO_URL} alt="Logo La Cantina del Barone" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full shrink-0 ring-1 ring-burgundy/15" />
            <span>
              <span className={scrolled ? "text-burgundy" : "text-cream"}>La Cantina</span>{" "}
              <span className="italic text-gold">del Barone</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testid}
                className={`text-[13px] font-medium tracking-wide transition-colors hover:text-gold ${
                  scrolled ? "text-ink/80" : "text-cream/85"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-5">
            <div
              className={`flex items-center rounded-full border px-1 py-0.5 text-[11px] font-mono ${
                dark ? "border-stoneMuted/30" : "border-cream/40"
              }`}
            >
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}-button`}
                  onClick={() => setLang(l)}
                  className={`px-2 py-1 rounded-full uppercase tracking-widest transition-colors ${
                    lang === l
                      ? "bg-gold text-burgundy font-medium"
                      : scrolled
                      ? "text-stoneMuted hover:text-ink"
                      : "text-cream/70 hover:text-cream"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <a
              href="#prenota"
              data-testid="nav-booking-link"
              className={`hidden sm:inline-flex items-center rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-wide transition-all hover:scale-[1.03] active:scale-[0.98] ${
                scrolled ? "bg-burgundy text-cream hover:bg-burgundy-light" : "bg-gold text-burgundy hover:bg-amberwarm"
              }`}
            >
              {t.nav.cta}
            </a>

            <button
              className="lg:hidden p-2"
              data-testid="nav-mobile-toggle"
              aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="w-6 h-6 text-gold" /> : <MenuIcon className={`w-6 h-6 ${scrolled ? "text-ink" : "text-cream"}`} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-burgundy flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {links.map((l, i) => (
                <span key={l.href} className="block overflow-hidden">
                  <motion.a
                    href={l.href}
                    data-testid={`${l.testid}-mobile`}
                    onClick={() => setOpen(false)}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.07, ease: EASE }}
                    className="block font-serif text-4xl text-cream py-2"
                  >
                    {l.label}
                  </motion.a>
                </span>
              ))}
              <span className="block overflow-hidden">
                <motion.a
                  href="#prenota"
                  data-testid="nav-booking-link-mobile"
                  onClick={() => setOpen(false)}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
                  className="block font-serif text-4xl italic text-gold py-2"
                >
                  {t.nav.cta}
                </motion.a>
              </span>
            </nav>
            <div className="mt-12 flex items-center gap-4">
              <a href={TEL_HREF} className="flex items-center gap-2 text-cream/80 text-sm border border-cream/25 rounded-full px-4 py-2.5">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
              <a href={lang === "en" ? WA_HREF_TEXT_EN : WA_HREF_TEXT} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cream/80 text-sm border border-cream/25 rounded-full px-4 py-2.5">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
